import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { MoreHorizontal } from 'lucide-react'
import { mutate } from '@/runtime'
import { useNow } from '@/hooks/useNow'
import type { TaskResource } from '@/kinds/task/apply'
import type { ProjectResource } from '@/kinds/project/apply'
import type { AgentRunResource } from '@/kinds/agentrun'
import { HandToAgentSheet } from '@/kinds/agentrun/components'
import { RunTag } from '@/components/RunStatus'
import { isOver } from '@/kinds/agentrun/schema'
import { AgentMenu } from '@/components/AgentMenu'
import { COLUMNS, boardOf, type BoardCard, type Column } from '@/lib/board'
import { useUi } from '@/state/ui'
import { IconButton, Sheet, Tag, cx } from '@/components/ui'

// A project as a board (To do, With an agent, In review, Done): its tasks and
// its coding agents where their work stands (lib/board.ts; Prime reads the same
// columns through its board tool). On a phone the columns sit side by side and
// snap one at a time; on a desk they are four across. Cards move by what you do
// to them (hand to an agent, merge, mark done), from a ⋯, not by dragging.

const CardMenu = ({
  card,
  project,
  onClose,
}: {
  card: BoardCard
  project: ProjectResource | undefined
  onClose: () => void
}) => {
  const navigate = useNavigate()
  const [handing, setHanding] = useState(false)
  const task = card.task as TaskResource
  const run = card.run
  const go = (to: string, params: Record<string, string>) => {
    onClose()
    void navigate({ to, params } as never)
  }
  const done = async () => {
    onClose()
    try {
      await mutate.action(task.id, 'complete', {})
    } catch (e) {
      useUi.getState().showFlash((e as Error).message, 3000)
    }
  }
  if (handing && project) return <HandToAgentSheet task={task} project={project} onClose={onClose} />
  const row = (label: string, onClick: () => void, hint?: string) => (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left px-3 min-h-14 rounded-[var(--radius-card-inner)] hover:bg-raised"
      >
        <span className="block text-[15px]">{label}</span>
        {hint && <span className="block text-xs text-faint">{hint}</span>}
      </button>
    </li>
  )
  const free = !run || isOver(run.spec.status)
  return (
    <Sheet open onClose={onClose} title={task.spec.title}>
      <ul className="flex flex-col gap-0.5 pb-2" data-testid="board-card-menu">
        {row('Open the task', () => go('/tasks/$id', { id: task.id }))}
        {free &&
          project &&
          row(
            'Hand it to a coding agent',
            () => setHanding(true),
            "It gets the task and the project's notes, pages and open tasks",
          )}
        {run && row('Open its agent', () => go('/agents/run/$id', { id: run.id }))}
        {run?.spec.pr_number
          ? row(`Review pull request #${run.spec.pr_number}`, () => go('/dev/review/$id', { id: run.id }))
          : null}
        {task.status.state !== 'done' && row('Mark it done', () => void done())}
      </ul>
    </Sheet>
  )
}

const Card = ({
  card,
  project,
  tag,
}: {
  card: BoardCard
  project: ProjectResource | undefined
  tag: boolean
}) => {
  const navigate = useNavigate()
  const [menu, setMenu] = useState(false)
  const run = card.run
  const title = card.task?.spec.title ?? run?.spec.title ?? run?.spec.name ?? ''
  const open = () =>
    card.task
      ? void navigate({ to: '/tasks/$id', params: { id: card.task.id } })
      : run && void navigate({ to: '/agents/run/$id', params: { id: run.id } })
  return (
    <li className="card relative" data-testid="board-card">
      <button type="button" onClick={open} className="w-full text-left px-3 py-2.5 pr-11">
        <span className="text-sm font-medium leading-snug line-clamp-3">{title}</span>
        {tag && project && (
          <span className="block mt-1">
            <Tag>{project.spec.title}</Tag>
          </span>
        )}
        {run && (
          <span className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <RunTag run={run} />
            {run.spec.pr_number ? (
              <Tag tone={run.spec.pr_state === 'merged' ? 'done' : undefined}>
                PR #{run.spec.pr_number}
                {run.spec.pr_state === 'merged' ? ' merged' : ''}
              </Tag>
            ) : null}
            {!card.task && <span className="text-[11px] text-faint">no task</span>}
          </span>
        )}
      </button>
      <span className="absolute right-1 top-1">
        <IconButton label="More" onClick={() => setMenu(true)}>
          <MoreHorizontal size={16} aria-hidden />
        </IconButton>
      </span>
      {menu &&
        (card.task ? (
          <CardMenu card={card} project={project} onClose={() => setMenu(false)} />
        ) : run ? (
          <AgentMenu run={run as AgentRunResource} open onClose={() => setMenu(false)} />
        ) : null)}
    </li>
  )
}

/** The four columns of a laid-out board; `projects` (by id) name each card's project when `tag` is on. */
export const BoardColumns = ({
  board,
  projects,
  tag,
  testid,
}: {
  board: Record<Column, BoardCard[]>
  projects: Map<string, ProjectResource>
  tag: boolean
  testid: string
}) => (
  <div
    // a phone: the columns scroll sideways inside their own box, from the page's 12px gutter
    // (wider than the gutter, the box pushed the whole page sideways and the tab bar with it)
    className="-mx-3 px-3 scroll-px-3 flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 md:mx-0 md:px-0 md:grid md:grid-cols-4 md:overflow-visible"
    data-testid={testid}
  >
    {COLUMNS.map((c) => (
      <section
        key={c.id}
        className={cx(
          'snap-start shrink-0 w-[82%] md:w-auto',
          'rounded-[var(--radius-card)] bg-raised/40 p-2',
        )}
        data-testid={`board-${c.id}`}
      >
        <h3 className="text-[13px] font-semibold text-muted px-1 pb-2">{c.title}</h3>
        {board[c.id].length === 0 ? (
          <p className="text-xs text-faint px-1 pb-2">Nothing here.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {board[c.id].map((card) => (
              <Card
                key={card.key}
                card={card}
                project={card.project ? projects.get(card.project) : undefined}
                tag={tag}
              />
            ))}
          </ul>
        )}
      </section>
    ))}
  </div>
)

export const ProjectBoard = ({
  project,
  tasks,
  runs,
}: {
  project: ProjectResource
  tasks: TaskResource[]
  runs: AgentRunResource[]
}) => {
  const now = useNow(60_000)
  const board = boardOf(project.id, tasks, runs, now)
  return (
    <BoardColumns
      board={board}
      projects={new Map([[project.id, project]])}
      tag={false}
      testid="project-board"
    />
  )
}
